import { useRouteError, isRouteErrorResponse, Link } from 'react-router-dom';
import { AlertTriangle, Home, RefreshCw } from 'lucide-react';

export function ErrorPage() {
  const error = useRouteError();

  let status: number | null = null;
  let title = 'Something went wrong';
  let message = 'An unexpected error occurred. Try refreshing the page.';

  if (isRouteErrorResponse(error)) {
    status = error.status;
    if (error.status === 404) {
      title = 'Page not found';
      message = "The page you're looking for doesn't exist or has been moved.";
    } else if (error.status === 401) {
      title = 'Unauthorized';
      message = 'You need to be signed in to view this page.';
    } else if (error.status === 403) {
      title = 'Access denied';
      message = "You don't have permission to view this page.";
    } else {
      message = error.statusText || message;
    }
  } else if (error instanceof Error) {
    message = error.message;
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface p-4">
      <div className="w-full max-w-sm text-center">
        {/* Icon */}
        <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl
                        bg-red-500/10 ring-1 ring-red-500/20">
          <AlertTriangle size={28} className="text-red-400" />
        </div>

        {/* Status code */}
        {status && (
          <p className="mb-1 text-[11px] font-semibold uppercase tracking-widest text-fg-3">
            Error {status}
          </p>
        )}

        {/* Title */}
        <h1 className="text-xl font-bold text-fg">{title}</h1>

        {/* Message */}
        <p className="mt-2 text-sm leading-relaxed text-fg-3">{message}</p>

        {/* Dev detail */}
        {import.meta.env.DEV && error instanceof Error && (
          <pre className="mt-4 overflow-auto rounded-xl border border-surface-3 bg-surface-2
                          px-4 py-3 text-left text-[11px] text-fg-3 max-h-36">
            {error.stack ?? error.message}
          </pre>
        )}

        {/* Actions */}
        <div className="mt-8 flex items-center justify-center gap-3">
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="inline-flex items-center gap-2 rounded-xl border border-surface-3
                       bg-surface-2 px-4 py-2 text-sm font-medium text-fg-2
                       hover:bg-surface-3 transition-colors"
          >
            <RefreshCw size={14} />
            Refresh
          </button>
          <Link
            to="/"
            className="inline-flex items-center gap-2 rounded-xl bg-brand-500 px-4 py-2
                       text-sm font-semibold text-white hover:bg-brand-600 transition-colors"
          >
            <Home size={14} />
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}
